import { useState, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useStudents } from '@/hooks/useStudents';
import { useStudentEngagement, EngagementStatus } from '@/hooks/useStudentEngagement';
import { Student } from '@/lib/types';
import { isOnline, relativeCoarse } from '@/lib/relative-time';

const ENG_COLOR: Record<EngagementStatus, string> = {
  green:  '#2E7D32',
  yellow: '#F9A825',
  red:    '#C8102E',
};
const ENG_LABEL: Record<EngagementStatus, string> = {
  green:  'Em dia',
  yellow: 'Atrasando',
  red:    'Sumido',
};

type StatusFilter = 'todos' | 'active' | 'waiting' | 'inactive';

const STATUS_LABEL: Record<Exclude<StatusFilter, 'todos'>, string> = {
  active:   'Ativo',
  waiting:  'Aguardando',
  inactive: 'Inativo',
};

function fmtBRL(v: number | null | undefined): string {
  if (v == null) return '—';
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function StudentsDesktop() {
  const { profile } = useAuth();
  const router = useRouter();
  const { students, loading } = useStudents(profile?.id ?? '');

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [engFilter, setEngFilter] = useState<EngagementStatus | 'todos'>('todos');
  const [search, setSearch] = useState('');

  const activeIds = students.filter(s => s.status === 'active').map(s => s.id);
  const { statuses: engagement } = useStudentEngagement(activeIds);

  const counts = useMemo(() => ({
    active:   students.filter(s => s.status === 'active').length,
    waiting:  students.filter(s => s.status === 'waiting').length,
    inactive: students.filter(s => s.status === 'inactive').length,
  }), [students]);

  const engCounts = useMemo(() => {
    const c = { green: 0, yellow: 0, red: 0 };
    for (const id of activeIds) {
      const e = engagement[id] ?? 'red';
      c[e]++;
    }
    return c;
  }, [activeIds, engagement]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return students
      .filter(s => {
        if (statusFilter !== 'todos' && s.status !== statusFilter) return false;
        if (engFilter !== 'todos') {
          if (s.status !== 'active') return false;
          if ((engagement[s.id] ?? 'red') !== engFilter) return false;
        }
        if (q) {
          const name = (s.full_name ?? '').toLowerCase();
          const email = s.email.toLowerCase();
          if (!name.includes(q) && !email.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email));
  }, [students, statusFilter, engFilter, search, engagement]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Alunos</Text>
          <Text style={styles.subtitle}>{counts.active} ativos · {counts.waiting} aguardando · {counts.inactive} inativos</Text>
        </View>
        <TouchableOpacity
          style={styles.newBtn}
          onPress={() => router.push('/(personal)/students/new' as never)}
          activeOpacity={0.7}
        >
          <Ionicons name="person-add-outline" size={14} color="#fff" />
          <Text style={styles.newBtnText}>Cadastrar aluno</Text>
        </TouchableOpacity>
      </View>

      {/* Banner destaque: alunos aguardando ativação (só aparece se houver) */}
      {counts.waiting > 0 && statusFilter !== 'waiting' && (
        <TouchableOpacity
          style={styles.waitingBanner}
          onPress={() => setStatusFilter('waiting')}
          activeOpacity={0.7}
        >
          <View style={styles.waitingBannerIcon}>
            <Ionicons name="time-outline" size={18} color="#E65100" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.waitingBannerTitle}>
              {counts.waiting === 1
                ? '1 aluno aguardando ativação'
                : `${counts.waiting} alunos aguardando ativação`}
            </Text>
            <Text style={styles.waitingBannerSub}>
              Toque pra ver e ativar
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color="#E65100" />
        </TouchableOpacity>
      )}

      {/* KPIs de engajamento (apenas pra alunos ativos) */}
      <View style={styles.kpiRow}>
        <Kpi label="Em dia" value={String(engCounts.green)} color={ENG_COLOR.green} sub="treinaram nos últimos 5d" />
        <Kpi label="Atrasando" value={String(engCounts.yellow)} color={ENG_COLOR.yellow} sub="5-10 dias parados" />
        <Kpi label="Sumidos" value={String(engCounts.red)} color={ENG_COLOR.red} sub="+10 dias sem treinar" />
        <Kpi label="Total ativo" value={String(counts.active)} sub="alunos pagantes" />
      </View>

      {/* Toolbar */}
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={14} color="#888" />
          <TextInput
            style={styles.searchInput}
            placeholder="Buscar por nome ou email…"
            placeholderTextColor="#aaa"
            value={search}
            onChangeText={setSearch}
          />
        </View>
        <View style={styles.filterChips}>
          <Chip label={`Ativos (${counts.active})`} active={statusFilter === 'active'} onPress={() => setStatusFilter('active')} />
          <Chip label={`Aguardando (${counts.waiting})`} active={statusFilter === 'waiting'} onPress={() => setStatusFilter('waiting')} />
          <Chip label={`Inativos (${counts.inactive})`} active={statusFilter === 'inactive'} onPress={() => setStatusFilter('inactive')} />
          <Chip label="Todos" active={statusFilter === 'todos'} onPress={() => setStatusFilter('todos')} />
        </View>
      </View>

      {/* Filtros de engajamento (apenas relevantes pra ativos) */}
      {statusFilter === 'active' && (
        <View style={styles.engFilterRow}>
          <Text style={styles.engFilterLabel}>Engajamento:</Text>
          <Chip label="Todos" active={engFilter === 'todos'} onPress={() => setEngFilter('todos')} />
          <Chip label={`Em dia (${engCounts.green})`} active={engFilter === 'green'} color={ENG_COLOR.green} onPress={() => setEngFilter('green')} />
          <Chip label={`Atrasando (${engCounts.yellow})`} active={engFilter === 'yellow'} color={ENG_COLOR.yellow} onPress={() => setEngFilter('yellow')} />
          <Chip label={`Sumidos (${engCounts.red})`} active={engFilter === 'red'} color={ENG_COLOR.red} onPress={() => setEngFilter('red')} />
        </View>
      )}

      {/* Tabela */}
      <View style={styles.table}>
        <View style={styles.thead}>
          <Text style={[styles.th, { flex: 3 }]}>Aluno</Text>
          <Text style={[styles.th, { flex: 1.2 }]}>Status</Text>
          <Text style={[styles.th, { flex: 1.2 }]}>Engajamento</Text>
          <Text style={[styles.th, { flex: 1.4 }]}>Objetivo</Text>
          <Text style={[styles.th, { flex: 1.2, textAlign: 'right' }]}>Mensalidade</Text>
          <Text style={[styles.th, { flex: 0.5, textAlign: 'right' }]}></Text>
        </View>

        {loading ? (
          <ActivityIndicator color="#C8102E" style={{ padding: 40 }} />
        ) : filtered.length === 0 ? (
          <Text style={styles.empty}>Nenhum aluno com esses filtros.</Text>
        ) : (
          filtered.map((s, idx) => (
            <StudentRow
              key={s.id}
              student={s}
              engagement={s.status === 'active' ? (engagement[s.id] ?? 'red') : undefined}
              zebra={idx % 2 === 1}
              onPress={() => router.push(`/(personal)/students/${s.id}` as never)}
            />
          ))
        )}
      </View>
    </ScrollView>
  );
}

// ─── Sub-componentes ───────────────────────────────────────────────────────

function Kpi({ label, value, color, sub }: { label: string; value: string; color?: string; sub?: string }) {
  return (
    <View style={styles.kpi}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={[styles.kpiValue, color && { color }]}>{value}</Text>
      {sub && <Text style={styles.kpiSub}>{sub}</Text>}
    </View>
  );
}

function Chip({ label, active, color, onPress }:
  { label: string; active: boolean; color?: string; onPress: () => void }) {
  return (
    <TouchableOpacity
      style={[styles.chip, active && { backgroundColor: color ?? '#C8102E' }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.chipText, active && { color: '#fff' }]}>{label}</Text>
    </TouchableOpacity>
  );
}

function StudentRow({ student, engagement, zebra, onPress }: {
  student: Student;
  engagement: EngagementStatus | undefined;
  zebra: boolean;
  onPress: () => void;
}) {
  const initials = (student.full_name ?? student.email).charAt(0).toUpperCase();
  const online = isOnline(student.last_seen_at);
  const presenceText = online ? 'online agora' : student.last_seen_at ? `Último login: ${relativeCoarse(student.last_seen_at)}` : null;
  return (
    <TouchableOpacity
      style={[styles.tr, zebra && { backgroundColor: '#FAFAFA' }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={[styles.td, { flex: 3, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        <View style={styles.avatarWrap}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{initials}</Text>
          </View>
          {online && <View style={styles.onlineDot} />}
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.cellName} numberOfLines={1}>{student.full_name ?? student.email}</Text>
          {student.full_name && <Text style={styles.cellSub} numberOfLines={1}>{student.email}</Text>}
          {presenceText && (
            <Text style={[styles.cellPresence, online && styles.cellPresenceOnline]} numberOfLines={1}>
              {presenceText}
            </Text>
          )}
        </View>
      </View>

      <View style={[styles.td, { flex: 1.2 }]}>
        <View style={[
          styles.statusBadge,
          student.status === 'active' && { backgroundColor: '#2E7D3222' },
          student.status === 'waiting' && { backgroundColor: '#F9A82522' },
          student.status === 'inactive' && { backgroundColor: '#88888822' },
        ]}>
          <Text style={[
            styles.statusBadgeText,
            student.status === 'active' && { color: '#2E7D32' },
            student.status === 'waiting' && { color: '#F9A825' },
            student.status === 'inactive' && { color: '#888' },
          ]}>
            {STATUS_LABEL[student.status]}
          </Text>
        </View>
      </View>

      <View style={[styles.td, { flex: 1.2 }]}>
        {engagement ? (
          <View style={styles.engRow}>
            <View style={[styles.engDot, { backgroundColor: ENG_COLOR[engagement] }]} />
            <Text style={styles.engText}>{ENG_LABEL[engagement]}</Text>
          </View>
        ) : (
          <Text style={styles.cellMuted}>—</Text>
        )}
      </View>

      <Text style={[styles.td, styles.cellText, { flex: 1.4 }]} numberOfLines={1}>
        {student.goal ?? '—'}
      </Text>

      <Text style={[styles.td, styles.cellMoney, { flex: 1.2, textAlign: 'right' }]}>
        {fmtBRL(student.monthly_fee)}
      </Text>

      <View style={[styles.td, { flex: 0.5, alignItems: 'flex-end' }]}>
        <Ionicons name="chevron-forward" size={16} color="#aaa" />
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 24, paddingBottom: 48 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 4 },

  newBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#C8102E', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  newBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  waitingBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#FFF8E1',
    borderWidth: 1.5, borderColor: '#F9A825',
    borderRadius: 10, padding: 14, marginBottom: 16,
  },
  waitingBannerIcon: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: '#FFE08233',
    alignItems: 'center', justifyContent: 'center',
  },
  waitingBannerTitle: { fontSize: 14, fontWeight: '700', color: '#E65100' },
  waitingBannerSub: { fontSize: 12, color: '#A86200', marginTop: 2 },

  kpiRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  kpi: {
    flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 14,
    borderWidth: 1, borderColor: '#EEE',
  },
  kpiLabel: { fontSize: 11, color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  kpiValue: { fontSize: 24, fontWeight: '800', color: '#1A1A1A' },
  kpiSub: { fontSize: 10, color: '#aaa', marginTop: 4 },

  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    minWidth: 240, flex: 1,
  },
  searchInput: { flex: 1, fontSize: 13, color: '#1A1A1A', outlineStyle: 'none' as never },

  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE' },
  chipText: { fontSize: 12, color: '#555', fontWeight: '600' },

  engFilterRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' },
  engFilterLabel: { fontSize: 11, color: '#888', fontWeight: '600', marginRight: 4 },

  table: { backgroundColor: '#fff', borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#EEE' },
  thead: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 16, paddingVertical: 12,
    backgroundColor: '#FAFAFA', borderBottomWidth: 1, borderBottomColor: '#EEE',
  },
  th: { fontSize: 11, fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 },

  tr: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#F2F2F2',
  },
  td: {},

  avatarWrap: { position: 'relative' },
  avatar: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: '#C8102E22',
    alignItems: 'center', justifyContent: 'center',
  },
  avatarText: { fontSize: 12, fontWeight: '700', color: '#C8102E' },
  // Bolinha de presenca: canto inferior direito do avatar quando online
  onlineDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#2E7D32',
    borderWidth: 2,
    borderColor: '#fff',
  },

  cellName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  cellSub: { fontSize: 11, color: '#888', marginTop: 2 },
  cellPresence: { fontSize: 10, color: '#aaa', marginTop: 1 },
  cellPresenceOnline: { color: '#2E7D32', fontWeight: '600' },
  cellText: { fontSize: 13, color: '#555' },
  cellMoney: { fontSize: 13, color: '#1A1A1A', fontWeight: '600' },
  cellMuted: { fontSize: 13, color: '#aaa' },

  statusBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusBadgeText: { fontSize: 11, fontWeight: '700' },

  engRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  engDot: { width: 8, height: 8, borderRadius: 4 },
  engText: { fontSize: 12, color: '#555' },

  empty: { fontSize: 13, color: '#888', textAlign: 'center', padding: 40 },
});
